import { baseAPICall } from './baseAPICall'
import { clientInstance } from '../utils/http-client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  Brand,
  BrandListResponse,
  BulkAssignBrandRequest,
  BulkAssignBrandResponse,
} from '../types/brands'

const BRANDS_KEY = 'brands'

// ============================================================================
// API Functions
// ============================================================================

/** Fetch paginated list of brands with optional search and ordering. */
export async function getBrands(params?: {
  search?: string
  page?: number
  page_size?: number
  ordering?: string
}) {
  return baseAPICall<BrandListResponse>(() =>
    clientInstance.get('products/catalog/brands/', { params })
  )
}

/** Fetch a single brand by ID. */
export async function getBrand(brandId: number) {
  return baseAPICall<Brand>(() =>
    clientInstance.get(`products/catalog/brands/${brandId}/`)
  )
}

/** Create a new brand with a name and optional image. */
export async function createBrand(data: {
  name: string
  image_id?: number | null
}) {
  return baseAPICall<Brand>(() =>
    clientInstance.post('products/catalog/brands/', data)
  )
}

/** Update an existing brand's name and/or image. */
export async function updateBrand(
  brandId: number,
  data: { name: string; image_id?: number | null }
) {
  return baseAPICall<Brand>(() =>
    clientInstance.put(`products/catalog/brands/${brandId}/`, data)
  )
}

/** Delete a brand by ID. */
export async function deleteBrand(brandId: number) {
  return baseAPICall<void>(() =>
    clientInstance.delete(`products/catalog/brands/${brandId}/`)
  )
}

/** Bulk assign or unlink a brand from multiple variants. */
export async function bulkAssignBrand(data: BulkAssignBrandRequest) {
  return baseAPICall<BulkAssignBrandResponse>(() =>
    clientInstance.post('products/catalog/variants/bulk-assign-brand/', data)
  )
}

// ============================================================================
// React Query Hooks
// ============================================================================

export const useGetBrands = (params?: {
  search?: string
  page?: number
  page_size?: number
  ordering?: string
  enabled?: boolean
}) => {
  const { enabled = true, ...queryParams } = params || {}
  return useQuery({
    queryKey: [BRANDS_KEY, queryParams],
    queryFn: () => getBrands(queryParams),
    enabled,
  })
}

export const useGetBrand = (brandId: number, enabled: boolean = true) =>
  useQuery({
    queryKey: [BRANDS_KEY, brandId],
    queryFn: () => getBrand(brandId),
    enabled,
  })

export const useCreateBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANDS_KEY] })
    },
  })
}

export const useUpdateBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      brandId,
      data,
    }: {
      brandId: number
      data: { name: string; image_id?: number | null }
    }) => updateBrand(brandId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANDS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['variants-catalog'] })
    },
  })
}

export const useDeleteBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BRANDS_KEY] })
      queryClient.invalidateQueries({ queryKey: ['variants-catalog'] })
    },
  })
}

export const useBulkAssignBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: bulkAssignBrand,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['variants-catalog'] })
      queryClient.invalidateQueries({ queryKey: [BRANDS_KEY] })
    },
  })
}
